import { useState, useCallback } from 'react';
import { Link } from 'react-router-dom';
import {
  AlertTriangleIcon,
  ArrowDownTrayIcon,
  ArrowPathIcon,
  ChevronLeftIcon,
  EllipsisHorizontalIcon,
  PencilIcon,
  PlusIcon,
  TrashIcon,
  UserIcon,
  UsersIcon,
} from '../../icons';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { Button, Modal } from '@safetyhub/ui';
import { MODAL_LAYER } from '../../modalLayers';
import { useStickyOffset } from '../../../hooks/useStickyOffset';
import {
  PhotoCardVertical,
  PreviousFindingCard,
  PreviousFindingDetailModal,
  ContributorCount,
  PhotoViewer,
  PhotoViewerWithEditor,
  BulkActionConfirmBottomSheet,
  ConfirmDialog,
  AddFindingSheet,
  HazardResolutionModal,
  ScheduleChangeRequestModal,
  ScheduleChangeRequestPanel,
  PhotoEmptyState,
} from '../shared';
import { SectionTabBar } from '../../common/SectionTabBar';
import { useTabScrollMemory } from '../../../hooks/useTabScrollMemory';
import type { BulkActionType, HazardEditState } from '../shared';
import type { BoundingBox, AnalysisStatus as LibAnalysisStatus, FindingSeverity as LibFindingSeverity } from '../../../lib/inspections';
import { ActionSheet, PullToRefresh } from '../touch';
import type { ActionSheetItem } from '../touch';
import { InspectionActionBar } from '../phone';
import { ReportTypeSelectModal } from '../../reports/ReportTypeSelectModal';
import { ReviewRequestResult, canStartScheduledInspection, getScheduleChangeRequests, updateInspection, InspectionCollaborator } from '../../../lib/inspections';
import {
  activeFindings,
  countActiveFindings,
  countSelectedMeasureFindings,
  hasSelectedMeasure,
} from '../../../lib/findingVisibility';
// 게이트 판정은 **shared가 정본**이다(서버 집행·목록·스케줄이 같은 함수를 쓴다 — 2026-10-09).
import {
  checkInspectionCompletability,
  resolveReportGate,
  signatureProgressLabel,
} from '@safetyhub/shared';
import { getRiskAssessmentConfigs } from '../../../lib/risk-assessment-config';
import { RiskConfigDropdown, shouldShowRiskConfigDropdown } from '../shared/RiskConfigDropdown';
import ParticipantsEditModal from '../../modals/ParticipantsEditModal';
import { InspectionViewer } from '../../../types/websocket';
import { useAuthStore } from '../../../stores/auth';
import { useToastStore } from '../../../stores/toast';
import { usePermissions } from '../../../hooks/usePermissions';
import { MOBILE_BOTTOM_PANEL_CLASS, SignatureModal } from '../../common';
import { PortalTooltip } from '../desktop';
import type {
  CompletionState,
  Photo,
  PreviousFinding,
  InspectionDetailData,
  FindingEditHandlers,
} from './inspection-detail.types';

interface InspectionDetailPhoneProps {
  inspection: InspectionDetailData;
  /** 현재 사용자 ID (권한 체크용) */
  currentUserId?: string;
  /** 조직 Owner 여부 (권한 체크용) */
  isOwner?: boolean;
  /** 현재 사용자가 점검 참여자 중 PRIMARY 역할인지 (measure 해제 override 권한) */
  isPrimary?: boolean;
  /** 점검 주 담당자 ID (권한 체크용) */
  inspectorId?: string;
  /** 읽기 전용 모드 (비담당자 접근 또는 완료된 점검) */
  isReadonly?: boolean;
  /** SCHEDULED 상태 여부 (읽기전용이지만 점검 시작 버튼 필요) */
  isScheduled?: boolean;
  /** 보고서 생성 여부 (생성된 경우 사진 추가 불가) */
  hasReport?: boolean;
  /** 이후 차수 점검이 존재하여 수정 불가 여부 */
  isLockedByLaterInspection?: boolean;
  /** AI 분석 활성화 여부 */
  enableAIAnalysis?: boolean;
  /** AI 분석 활성화 여부 변경 */
  setEnableAIAnalysis?: (enabled: boolean) => void;
  /** AI 분析 중점 카테고리 */
  focusCategories?: string[];
  /** AI 분析 중점 카테고리 변경 */
  setFocusCategories?: (categories: string[]) => void;
  onAddPhoto: () => void;
  /** 우수사례 사진 추가 */
  onAddBestPracticePhoto?: () => void;
  /** 카메라 촬영 모달 열기 */
  onOpenCamera?: () => void;
  onPhotoReview: (photoId: string, reviewed: boolean) => void;
  /** 선택된 사진들 검토 완료 */
  onSelectiveReview: (photoIds: string[]) => void;
  /** 선택된 사진들 삭제 */
  onSelectiveDelete: (photoIds: string[]) => void;
  /** 선택된 사진들 재분석 */
  onBulkReanalyze: (photoIds: string[]) => void;
  onDeleteInspection: () => void;
  onResolveFindings: (findingId: string, data: {
    resolutionPhotoUrl?: string;
    resolutionPhotoId?: string;
    resolutionNotes?: string;
  }) => void;
  onUnresolveFindings: (findingId: string) => void;
  onRequestResolutionPhoto: (findingId: string) => void;
  onPhotoDelete: (photoId: string) => void;
  onPhotoReanalyze: (photoId: string) => void;
  /** 분석 범위 지정 모달의 대상을 지목한다(모달은 점검 상세 페이지가 하나만 들고 있다) */
  onPhotoReanalyzeWithRegion: (photoId: string) => void;
  onPhotoDownload: (photoId: string) => void;
  onBack: () => void;
  onRefresh: () => Promise<void>;
  isLoading?: boolean;
  isReanalyzing?: boolean;
  /** 재분석 진행 상황 */
  reanalysisProgress?: { current: number; total: number };
  /** 발견사항 편집 핸들러 */
  findingEditHandlers?: FindingEditHandlers;
  /** 현재 점검을 보고 있는 사용자 목록 */
  viewers?: InspectionViewer[];
  /** 검토 요청 */
  onRequestReview?: () => Promise<ReviewRequestResult | null>;
  /** 발견사항 추가 (새 발견사항 ID 반환) */
  onAddFinding?: (photoId: string, data: {
    category: string;
    description: string;
  }) => Promise<string | void>;
  /** 발견사항 보고서 포함 선택 기능 활성화 여부 */
  findingSelectionEnabled?: boolean;
  /** 개선대책 보고서 포함 여부 토글 */
  onToggleMeasureSelection?: (findingId: string, measure: string, excluded: boolean) => void;
  /** 점검완료 서명 워크플로우 */
  completion?: CompletionState;
  /** 예약 점검 시작 핸들러 */
  onStartScheduled?: () => void;
  /** 사진 메타 업데이트 핸들러 */
  onPhotoMetaUpdate?: (photoId: string, updates: {
    location?: { id: string; name: string } | null;
    detailLocation?: { id: string; name: string } | null;
    equipment?: { id: string; name: string; managementNumber?: string | null; processType?: string | null } | null;
    processType?: string | null;
  }) => void;
  /** 현장 담당자 모달 열기 */
  onOpenSiteContactsModal?: () => void;
}

export function InspectionDetailPhone({
  inspection,
  currentUserId,
  isOwner = false,
  isPrimary = false,
  inspectorId,
  isReadonly = false,
  isScheduled = false,
  hasReport = false,
  isLockedByLaterInspection = false,
  enableAIAnalysis = true,
  setEnableAIAnalysis,
  focusCategories = [],
  setFocusCategories: _setFocusCategories,
  onAddPhoto,
  onAddBestPracticePhoto,
  onOpenCamera,
  onPhotoReview,
  onSelectiveReview,
  onSelectiveDelete,
  onBulkReanalyze,
  onDeleteInspection,
  onResolveFindings: _onResolveFindings,
  onUnresolveFindings: _onUnresolveFindings,
  onRequestResolutionPhoto: _onRequestResolutionPhoto,
  onPhotoDelete,
  onPhotoReanalyze,
  onPhotoReanalyzeWithRegion,
  onPhotoDownload,
  onBack,
  onRefresh,
  isLoading = false,
  isReanalyzing = false,
  reanalysisProgress,
  findingEditHandlers,
  viewers = [],
  onRequestReview,
  onAddFinding,
  findingSelectionEnabled = false,
  onToggleMeasureSelection,
  completion,
  onStartScheduled,
  onPhotoMetaUpdate,
  onOpenSiteContactsModal,
}: InspectionDetailPhoneProps) {
  const { currentMembership } = useAuthStore();
  const { canManageInspections, isSiteAssigned } = usePermissions(inspection.siteId);
  const queryClient = useQueryClient();
  const companyId = currentMembership?.companyId;
  const { showToast } = useToastStore();

  // 스케줄 변경 요청 모달 상태
  const [showScheduleChangeModal, setShowScheduleChangeModal] = useState(false);

  // 스케줄 변경 요청 목록 조회 (SCHEDULED 상태에서만)
  const { data: scheduleChangeRequestsData, refetch: refetchScheduleChangeRequests } = useQuery({
    queryKey: ['scheduleChangeRequests', inspection.id],
    queryFn: () => getScheduleChangeRequests(inspection.id),
    enabled: !!inspection.id && inspection.status === 'SCHEDULED',
  });
  const scheduleChangeRequests = scheduleChangeRequestsData?.success && scheduleChangeRequestsData.data
    ? scheduleChangeRequestsData.data
    : [];

  // 조직의 활성 위험성평가 config 목록 (드롭다운용)
  const { data: riskConfigsData } = useQuery({
    queryKey: ['riskAssessmentConfigs'],
    queryFn: getRiskAssessmentConfigs,
    staleTime: 5 * 60 * 1000,
  });
  const allRiskConfigs = riskConfigsData?.data ?? [];

  // 점검별 위험성평가 config 변경 핸들러
  const handleRiskConfigChange = useCallback(async (configId: string | null) => {
    if (!companyId) return;
    await updateInspection(inspection.id, { riskAssessmentConfigId: configId }, companyId);
    await queryClient.invalidateQueries({ queryKey: ['inspection', inspection.id] });
  }, [inspection.id, companyId, queryClient]);

  // 변경 요청 버튼 표시 조건 (안전관리자 + Owner 배정 점검)
  const canRequestScheduleChange = inspection.status === 'SCHEDULED'
    && !!inspection.assignedById
    && inspectorId === currentUserId
    && !isOwner;

  // 점검완료 서명 모달
  const [showSignatureModal, setShowSignatureModal] = useState(false);
  // 점검완료 취소/요청 모달
  const [showCompletionActionModal, setShowCompletionActionModal] = useState(false);
  const [showCancelConfirm, setShowCancelConfirm] = useState(false);

  // 점검완료 잠금 상태: 서명 완료 시 콘텐츠 편집 불가
  const isCompletionLocked = !!completion?.isMyCompleted;

  // 사진/발견사항 편집 공통 비활성화 조건
  const isEditDisabled = hasReport || isLockedByLaterInspection;

  // 사진 배열 분리 (위험요인 / 우수사례)
  const hazardPhotos = inspection.photos.filter(p => p.purpose !== 'BEST_PRACTICE');
  const bestPracticePhotos = inspection.photos.filter(p => p.purpose === 'BEST_PRACTICE');

  // 점검 헤더 하단 위치 → 섹션 sticky top으로 사용
  const { ref: headerRef, stickyTop: sectionStickyTop } = useStickyOffset<HTMLElement>();
  // 탭 바 하단 위치 → 금번 점검 섹션 헤더의 sticky top으로 사용
  const { ref: tabBarRef, stickyTop: tabBarBottom } = useStickyOffset<HTMLDivElement>();

  // 편집 가능 여부 (읽기 전용이 아닌 경우 - COMPLETED는 상태 표시일 뿐 편집 제한 아님)
  const canEdit = !isReadonly;

  // 발견사항 편집 가능 여부 (보고서가 생성된 경우 또는 점검완료 잠금 시 편집 불가)
  const canEditFinding = canEdit && !isEditDisabled && !isCompletionLocked;

  // 평가 방식 편집 가능 여부 — 완료된 점검은 방식을 잠근다(표시는 유지). 2026-08-06 사용자 확정.
  const canEditRiskConfig = canEdit && inspection.status !== 'COMPLETED';

  // 사진별 검토 권한 체크 (사진 업로더 또는 Owner만 검토 가능)
  // hasReport일 때는 UI에서 disabled + 툴팁으로 표시
  const canReviewPhoto = (photo: Photo): boolean => {
    if (isReadonly) return false;
    const isPhotoUploader = photo.addedById === currentUserId;
    return isPhotoUploader || isOwner;
  };

  // 선택/검토 대상 사진 목록 (Owner면 전체, 아니면 본인 사진만)
  const selectablePhotos = isOwner
    ? inspection.photos
    : inspection.photos.filter((p) => p.addedById === currentUserId);
  const selectablePhotoIds = new Set(selectablePhotos.map((p) => p.id));
  const [activeInspectionTab, setActiveInspectionTab] = useState<string>('current');
  const { handleTabChange: handleInspectionTabChange } = useTabScrollMemory();
  const onInspectionTabChange = useCallback(
    (newTab: string) => handleInspectionTabChange(activeInspectionTab, newTab, setActiveInspectionTab),
    [activeInspectionTab, handleInspectionTabChange],
  );
  const [previousFindingDetail, setPreviousFindingDetail] = useState<PreviousFinding | null>(null);
  const [selectedPhotoId, setSelectedPhotoId] = useState<string | null>(null);
  const [selectedPhotoIds, setSelectedPhotoIds] = useState<Set<string>>(new Set());
  const [isActionSheetOpen, setIsActionSheetOpen] = useState(false);
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [showReportModal, setShowReportModal] = useState(false);
  // 위험영역 표시 상태 (공통, 기본값: true)
  const [showHazardOverlay, setShowHazardOverlay] = useState(true);
  // 그리기 메모 표시 상태
  const [showAnnotationOverlay, setShowAnnotationOverlay] = useState(true);
  // 강조된 발견사항 ID (새로 추가된 발견사항 선택용)
  const [highlightedFindingId, setHighlightedFindingId] = useState<string | null>(null);
  const [hazardSectionExpanded, setHazardSectionExpanded] = useState(true);
  const [bpSectionExpanded, setBpSectionExpanded] = useState(true);

  // 위험 영역 에디터 상태
  const [hazardEditorState, setHazardEditorState] = useState<{
    isOpen: boolean;
    currentIndex: number;
  }>({
    isOpen: false,
    currentIndex: 0,
  });

  // 위험 영역 편집 상태 (findingId -> 편집 상태)
  const [hazardEdits, setHazardEdits] = useState<Map<string, HazardEditState>>(new Map());

  // 발견사항 추가 시트 상태
  const [addFindingSheetState, setAddFindingSheetState] = useState<{
    isOpen: boolean;
    photoId: string | null;
    isLoading: boolean;
    mode: 'hazard' | 'best-practice';
  }>({
    isOpen: false,
    photoId: null,
    isLoading: false,
    mode: 'hazard',
  });

  // 위험요인 해소 모달 상태
  const [hazardResolutionState, setHazardResolutionState] = useState<{
    isOpen: boolean;
    photoId: string | null;
    preSelectedFindingId?: string;
  }>({ isOpen: false, photoId: null });

  // (「이미 생성된 보고서 목록」을 여기서 조회하지 않는다 — 중복 생성 가드는 문서 유형 선택 모달이
  //  템플릿 키 축으로 직접 조회한다.)

  // 점검 담당자 편집 모달 상태
  const [participantsModalOpen, setParticipantsModalOpen] = useState(false);

  // 검토 요청 상태
  const [isRequestingReview, setIsRequestingReview] = useState(false);

  // 일괄 작업 확인 바텀시트 상태
  const [bulkActionSheet, setBulkActionSheet] = useState<{
    isOpen: boolean;
    actionType: BulkActionType;
    isLoading: boolean;
  }>({
    isOpen: false,
    actionType: 'reanalyze',
    isLoading: false,
  });
  const [photoViewerState, setPhotoViewerState] = useState<{
    isOpen: boolean;
    currentIndex: number;
    photos: { id: string; url: string; caption?: string }[];
    forceReadonly?: boolean;
    overrideInspectionId?: string;
    skipAnnotation?: boolean;
  }>({
    isOpen: false,
    currentIndex: 0,
    photos: [],
  });

  // 계산된 값들
  const totalPhotos = inspection.photos.length;
  const reviewablePhotos = inspection.photos.filter(
    (p) => p.analysisStatus === 'COMPLETED'
  );
  const reviewedPhotos = inspection.photos.filter((p) => p.isReviewed);
  // 이전 미해결 항목 (이번 점검에서 해결된 것도 포함 - 서버에서 이미 필터링됨)
  const previousFindings = inspection.previousFindings;

  // 미입력 발견사항 수 (조치결과가 이번 점검에서 입력되지 않은 것)
  const unreviewedFindingsCount = previousFindings.filter(
    (f) => f.lastActionInspectionId !== inspection.id
  ).length;

  const contributorCount = (inspection.contributors ?? []).filter(
    (p) => p.role === 'SECONDARY'
  ).length;

  // 상세 상태 카운트
  const analyzingPhotos = inspection.photos.filter(
    (p) => p.analysisStatus === 'PENDING' || p.analysisStatus === 'ANALYZING'
  );
  const pendingReviewPhotos = inspection.photos.filter(
    (p) => p.analysisStatus === 'COMPLETED' && !p.isReviewed
  );
  // 다른 사용자가 업로드한 미검토 사진 (검토 요청 대상)
  const pendingReviewPhotosByOthers = inspection.photos.filter(
    (p) => p.analysisStatus === 'COMPLETED' && !p.isReviewed && p.addedById && p.addedById !== currentUserId
  );

  // 점검 완료 가능 여부 확인 (공통 유틸리티 함수 사용)
  const completability = checkInspectionCompletability({
    totalPhotos: reviewablePhotos.length,
    reviewedPhotos: reviewedPhotos.length,
    unreviewedPreviousFindingsCount: unreviewedFindingsCount,
  }, { skipPhotoReviewCheck: findingSelectionEnabled });
  const canComplete = completability.canComplete;

  // 관리 권한자 또는 주 담당자 여부. ⚠️ prop `isOwner`는 이름만 남은 호환 축이다 — 값은
  // `useInspectionDetailPage`가 `isOrgOwner || (배정 현장 && inspection.manage)`로 넓힌 것이고
  // (2026-09-24 수용, company-rbac P5 라운드 ②), 「조직 Owner」로 읽으면 범위를 좁게 오독한다.
  const isInspectorOrOwner = inspectorId === currentUserId || isOwner;
  // 점검 담당자 편집 버튼 노출 여부 (관리 권한자 또는 주 담당자)
  const canChangeInspector = isInspectorOrOwner && !isReadonly;
  // 점검 담당자 편집 가능 여부: status !== COMPLETED && !hasReport
  const canEditParticipants = canChangeInspector && inspection.status !== 'COMPLETED' && !hasReport;
  // 현재 점검 참여자 목록 (서버 collaborators 직접 사용)
  const currentParticipants: InspectionCollaborator[] = inspection.collaborators ?? [];
  // 검토 요청 가능 여부 (다른 사용자가 업로드한 미검토 사진이 있을 때만)
  const canRequestReview = isInspectorOrOwner && !isReadonly && pendingReviewPhotosByOthers.length > 0;
  // 점검 삭제 권한 (주 담당자 또는 관리 권한자 — 위 isOwner 주석의 넓힌 축)
  const canDeleteInspection = inspection.inspectorId === currentUserId || isOwner;

  // 보고서 생성 권한: 주 담당자, Owner, 또는 현장 배정 담당자
  const canCreateReport = isInspectorOrOwner || isSiteAssigned;
  // 선택된 개선대책이 1개 이상 있는지 (피처 플래그 ON 시) — **살아 있는 것만** 센다.
  // 왜 세는 쪽이 걸러야 하는지, 파생이 둘·게이트가 둘인 것(이 값 ↔ 훅의 `hasAnyMeasures`)은
  // `lib/findingVisibility.ts` 모듈 JSDoc이 정본이다. 배선은
  // `lib/__tests__/findingVisibilityWiring.test.ts`가 지킨다.
  const hasAnySelectedMeasure = !findingSelectionEnabled
    || inspection.photos.some(p => hasSelectedMeasure(p.findings));

  // 보고서 포함 발견사항 수 (findingSelectionEnabled 시) — 분자·분모 둘 다 살아 있는 것만.
  // 안 걸면 「N/M건 포함」이 지운 발견사항까지 세어 사용자가 보는 숫자가 거짓이 된다.
  const totalFindingsCount = findingSelectionEnabled
    ? inspection.photos.reduce((sum, p) => sum + countActiveFindings(p.findings), 0)
    : 0;
  const includedFindingsCount = findingSelectionEnabled
    ? inspection.photos.reduce((sum, p) => sum + countSelectedMeasureFindings(p.findings), 0)
    : 0;

  // 보고서 생성 가능 조건: 권한이 있고 모든 분석 완료 사진이 검토 완료됨
  // 보고서 생성 게이트 + 그 사유 — **식과 문구 둘 다 `lib/reportGate`가 정본**이다.
  // 종전엔 `completionSignatureEnabled`가 상수 `true`라 `(true || hasAnySelectedMeasure)`가
  // **OR 단축으로 선택 여부를 아예 안 봤다**(선택 0건이어도 버튼 활성 — Fold와 갈렸다).
  // 경위·정본 선택 근거는 그 모듈 JSDoc. 여기서 식을 다시 쓰지 말 것.
  const reportGate = resolveReportGate({
    canCreateReport,
    canComplete,
    completabilityReasons: completability.reasons,
    findingSelectionEnabled,
    hasAnySelectedMeasure,
    completion: completion
      ? {
          allCompleted: completion.allCompleted,
          completedCount: completion.completedCount,
          totalCount: completion.totalCount,
        }
      : null,
  });
  const canGenerateReport = reportGate.canGenerate;

  const selectedCount = selectedPhotoIds.size;

  // 선택 토글 핸들러
  const handleSelectionChange = (photoId: string, checked: boolean) => {
    setSelectedPhotoIds((prev) => {
      const newSet = new Set(prev);
      if (checked) {
        newSet.add(photoId);
      } else {
        newSet.delete(photoId);
      }
      return newSet;
    });
  };

  // 일괄 작업 확인 바텀시트 열기
  const openBulkActionSheet = (actionType: BulkActionType) => {
    if (selectedCount > 0) {
      setBulkActionSheet({ isOpen: true, actionType, isLoading: false });
    }
  };

  // 일괄 작업 확인 바텀시트 닫기
  const closeBulkActionSheet = () => {
    setBulkActionSheet((prev) => ({ ...prev, isOpen: false }));
  };

  // 일괄 작업 실행
  const handleBulkActionConfirm = async () => {
    const photoIds = Array.from(selectedPhotoIds);
    setBulkActionSheet((prev) => ({ ...prev, isLoading: true }));

    try {
      switch (bulkActionSheet.actionType) {
        case 'reanalyze':
          onBulkReanalyze(photoIds);
          break;
        case 'delete':
          onSelectiveDelete(photoIds);
          break;
        case 'review':
          onSelectiveReview(photoIds);
          break;
      }
      // 작업 후 선택 해제
      setSelectedPhotoIds(new Set());
      closeBulkActionSheet();
    } catch {
      setBulkActionSheet((prev) => ({ ...prev, isLoading: false }));
    }
  };

  // 선택 재분석 핸들러
  const handleSelectiveReanalyze = () => {
    openBulkActionSheet('reanalyze');
  };

  // 선택 검토 완료 핸들러
  const handleSelectiveReview = () => {
    openBulkActionSheet('review');
  };

  // 선택 삭제 핸들러
  const handleSelectiveDelete = () => {
    openBulkActionSheet('delete');
  };

  // 점검 담당자 편집 모달 열기
  const handleOpenParticipantsModal = () => {
    setParticipantsModalOpen(true);
  };

  // 검토 요청 실행
  const handleRequestReviewClick = async () => {
    if (!onRequestReview) return;
    setIsRequestingReview(true);
    try {
      const result = await onRequestReview();
      if (result) {
        alert(`${result.notifiedUsers.length}명에게 검토 요청을 보냈습니다.`);
      }
    } finally {
      setIsRequestingReview(false);
    }
  };

  // 전체 선택/해제 핸들러 (본인이 업로드한 사진만)
  const handleSelectAll = () => {
    const mySelectedCount = Array.from(selectedPhotoIds).filter((id) => selectablePhotoIds.has(id)).length;
    if (mySelectedCount === selectablePhotos.length && selectablePhotos.length > 0) {
      setSelectedPhotoIds(new Set());
    } else {
      setSelectedPhotoIds(new Set(selectablePhotos.map((p) => p.id)));
    }
  };

  // 본인이 업로드한 사진이 모두 선택되었는지 확인
  const mySelectedCount = Array.from(selectedPhotoIds).filter((id) => selectablePhotoIds.has(id)).length;
  const isAllSelected = mySelectedCount === selectablePhotos.length && selectablePhotos.length > 0;

  // 발견사항 추가 시트 열기
  const handleOpenAddFindingSheet = (photoId: string) => {
    const photo = inspection.photos.find(p => p.id === photoId);
    const mode = photo?.purpose === 'BEST_PRACTICE' ? 'best-practice' as const : 'hazard' as const;
    setAddFindingSheetState({ isOpen: true, photoId, isLoading: false, mode });
    setIsActionSheetOpen(false);
  };

  // 발견사항 추가 시트 닫기
  const handleCloseAddFindingSheet = () => {
    setAddFindingSheetState({ isOpen: false, photoId: null, isLoading: false, mode: 'hazard' });
  };

  // 발견사항 추가 제출
  const handleAddFindingSubmit = async (data: {
    category: string;
    description: string;
  }) => {
    if (!addFindingSheetState.photoId || !onAddFinding) return;
    const photoId = addFindingSheetState.photoId;
    const isBpMode = addFindingSheetState.mode === 'best-practice';

    setAddFindingSheetState((prev) => ({ ...prev, isLoading: true }));
    try {
      const newFindingId = await onAddFinding(photoId, data);
      handleCloseAddFindingSheet();

      // 새 발견사항이 추가되면 선택 상태로 설정하고 위험영역 표시 활성화 (위험요인만)
      if (newFindingId && !isBpMode) {
        setHighlightedFindingId(newFindingId);
        setShowHazardOverlay(true);
      }
    } finally {
      setAddFindingSheetState((prev) => ({ ...prev, isLoading: false }));
    }
  };

  // 사진 뷰어 열기
  const openPhotoViewer = (photos: Photo[], index: number) => {
    setPhotoViewerState({
      isOpen: true,
      currentIndex: index,
      // 캡션은 **세는 자리**다 — 지운 발견사항을 세면 「3건 발견」인데 화면엔 1건만 보인다.
      photos: photos.map((p) => {
        const activeCount = countActiveFindings(p.findings);
        return {
          id: p.id,
          url: p.url,
          caption: activeCount > 0 ? `${activeCount}건 발견` : undefined,
        };
      }),
    });
  };

  // 위험 영역 변경 핸들러
  const handleHazardChange = (findingId: string, boundingBox: BoundingBox | null) => {
    setHazardEdits((prev) => {
      const newMap = new Map(prev);
      const existing = newMap.get(findingId);

      const allFindings = inspection.photos.flatMap((p) =>
        p.findings.map((f) => ({
          ...f,
          aiAnalysisDetail: (f as any).aiAnalysisDetail,
        }))
      );
      const fullFinding = allFindings.find((f) => f.id === findingId);
      const originalBox = (fullFinding as any)?.aiAnalysisDetail?.boundingBox || null;

      if (boundingBox === null) {
        newMap.set(findingId, {
          findingId,
          original: existing?.original || originalBox,
          current: null,
          isDeleted: true,
        });
      } else {
        newMap.set(findingId, {
          findingId,
          original: existing?.original || originalBox,
          current: boundingBox,
          isDeleted: false,
        });
      }

      return newMap;
    });
  };

  // 위험 영역 되돌리기 핸들러 (원본 AI 분석 결과로 복원)
  const handleHazardRestore = async (findingId: string) => {
    // 1. 로컬 편집 상태에서 원본 가져오기 (실시간 편집 중인 경우)
    const edit = hazardEdits.get(findingId);
    let originalBoundingBox = edit?.original;

    // 2. 로컬 상태에 없으면 서버의 modifications에서 원본 가져오기 (페이지 재방문 시)
    if (!originalBoundingBox) {
      const finding = inspection?.photos
        .flatMap(p => p.findings)
        .find(f => f.id === findingId);
      const boundingBoxMod = finding?.modifications?.modifiedFields?.find(f => f.field === 'boundingBox');
      if (boundingBoxMod?.originalValue) {
        originalBoundingBox = boundingBoxMod.originalValue as BoundingBox;
      }
    }

    if (!originalBoundingBox) return;

    // API로 원본 boundingBox 복원
    if (findingEditHandlers?.onUpdateFinding) {
      try {
        // 서버에 필요한 필드만 전송 (label 등 추가 속성 제외)
        const sanitizedBox = {
          x: originalBoundingBox.x,
          y: originalBoundingBox.y,
          width: originalBoundingBox.width,
          height: originalBoundingBox.height,
        };
        await findingEditHandlers.onUpdateFinding(findingId, { boundingBox: sanitizedBox }, { isRevert: true });
      } catch (error) {
        console.error('위험 영역 되돌리기 실패:', error);
        return;
      }
    }

    // 로컬 편집 상태 초기화
    setHazardEdits((prev) => {
      const newMap = new Map(prev);
      newMap.delete(findingId);
      return newMap;
    });
  };

  // 위험 영역 변경 완료 핸들러 (드래그 종료 시 서버 저장용)
  const handleHazardChangeEnd = async (findingId: string, boundingBox: BoundingBox) => {
    if (!findingEditHandlers?.onUpdateFinding) return;
    try {
      // 서버에 필요한 필드만 전송 (label 등 추가 속성 제외)
      const sanitizedBox = {
        x: boundingBox.x,
        y: boundingBox.y,
        width: boundingBox.width,
        height: boundingBox.height,
      };
      await findingEditHandlers.onUpdateFinding(findingId, { boundingBox: sanitizedBox });
      // 편집 상태는 유지 (되돌리기 버튼 표시용)
    } catch (error) {
      console.error('위험 영역 저장 실패:', error);
    }
  };

  // 위험 영역 삭제 저장 핸들러 (삭제 버튼 클릭 시 서버 저장용)
  const handleHazardDeleteSave = async (findingId: string) => {
    if (!findingEditHandlers?.onUpdateFinding) return;
    try {
      await findingEditHandlers.onUpdateFinding(findingId, { boundingBox: null });
      // 편집 상태는 유지 (되돌리기 버튼 표시용)
    } catch (error) {
      console.error('위험 영역 삭제 저장 실패:', error);
    }
  };

  // PhotoViewerWithEditor용 데이터 변환
  const photosWithFindings = inspection.photos.map((photo) => ({
    photo: {
      id: photo.id,
      inspectionId: inspection.id,
      url: photo.url,
      thumbnailUrl: photo.thumbnailUrl,
      status: photo.status,
      analysisStatus: photo.analysisStatus as LibAnalysisStatus,
      isReviewed: photo.isReviewed,
      createdAt: inspection.createdAt,
    },
    findings: photo.findings.map((f) => ({
      id: f.id,
      inspectionId: inspection.id,
      photoId: photo.id,
      category: f.category,
      subCategory: f.subCategory,
      description: f.description,
      severity: f.severity as LibFindingSeverity,
      aiGenerated: f.aiGenerated,
      resolutionStatus: 'PENDING' as const,
      createdAt: inspection.createdAt,
      deletedAt: f.deletedAt,
      aiAnalysisDetail: (f as any).aiAnalysisDetail,
    })),
  }));

  // 선택된 사진에 대한 액션 시트 아이템
  const getPhotoActionItems = () => {
    const photo = inspection.photos.find((p) => p.id === selectedPhotoId);
    if (!photo) return [];

    const isBestPractice = photo.purpose === 'BEST_PRACTICE';

    const items: ActionSheetItem[] = [
      {
        id: 'view',
        label: '그리기 메모',
        icon: PencilIcon,
        onClick: () => {
          const index = inspection.photos.findIndex((p) => p.id === selectedPhotoId);
          openPhotoViewer(inspection.photos, index);
        },
      },
      {
        id: 'download',
        label: '다운로드',
        icon: ArrowDownTrayIcon,
        onClick: () => onPhotoDownload(selectedPhotoId!),
      },
    ];

    // 편집 기능 (보고서 미생성 + 점검완료 미잠금 시에만)
    if (canEdit && !isEditDisabled && !isCompletionLocked) {
      // 발견사항 추가 (우수사례 사진 제외)
      if (onAddFinding && !isBestPractice) {
        items.push({
          id: 'add-finding',
          label: '발견사항 추가',
          icon: PlusIcon,
          onClick: () => handleOpenAddFindingSheet(selectedPhotoId!),
        });
      }
      // 위험요인 해소 (우수사례 사진 제외) — 이후 차수 잠금과 무관하게 해소 가능
      if (!(hasReport || isCompletionLocked) && canEdit && !isBestPractice) {
        // ⚠️ 지역 변수를 `activeFindings`로 두면 **import한 공용 술어를 가린다**(같은 이름이
        // 이 블록에서 섀도잉한다) — 그래서 `active`로 받는다. 되돌려 이름을 맞추지 말 것.
        const active = activeFindings(photo.findings);
        if (active.length > 0) {
          const allResolved = active.every(f => f.resolutionStatus === 'COMPLIED');
          items.push({
            id: 'resolve-hazard',
            label: allResolved ? '위험요인 해소 (모두 해소됨)' : '위험요인 해소',
            icon: PlusIcon,
            onClick: () => {
              if (!allResolved) {
                setHazardResolutionState({ isOpen: true, photoId: photo.id });
                setIsActionSheetOpen(false);
              }
            },
            disabled: allResolved,
          });
        }
      }
      items.push({
        id: 'reanalyze',
        label: '다시 분석',
        icon: ArrowPathIcon,
        onClick: () => onPhotoReanalyze(selectedPhotoId!),
      });
      items.push({
        id: 'reanalyze-region',
        label: '범위 지정 후 다시 분석',
        icon: ArrowPathIcon,
        onClick: () => onPhotoReanalyzeWithRegion(selectedPhotoId!),
      });
      items.push({
        id: 'delete',
        label: '삭제',
        icon: TrashIcon,
        onClick: () => onPhotoDelete(selectedPhotoId!),
        danger: true,
      } as any);
    }

    return items;
  };

  // 메인 메뉴 아이템
  const menuItems = [
    // 현장 담당자 선택/서명 (읽기 전용 아닌 경우)
    ...(!isReadonly && onOpenSiteContactsModal
      ? [{
          id: 'site-contacts',
          label: '현장 담당자',
          icon: UserIcon,
          onClick: onOpenSiteContactsModal,
        }]
      : []),
    // 점검 담당자 편집 (권한 있으면 표시, 편집 불가 상태면 disabled + 사유)
    ...(canChangeInspector
      ? [{
          id: 'edit-participants',
          label: '점검 담당자',
          icon: UsersIcon,
          onClick: handleOpenParticipantsModal,
          disabled: !canEditParticipants,
          disabledReason: !canEditParticipants
            ? (hasReport
                ? '보고서가 생성되어 편집할 수 없습니다'
                : '점검 완료 후에는 편집할 수 없습니다')
            : undefined,
        }]
      : []),
    // 검토 요청 (다른 사용자가 업로드한 미검토 사진이 있을 때만, findingSelectionEnabled 시 숨김)
    ...(!findingSelectionEnabled && canRequestReview && onRequestReview
      ? [{
          id: 'request-review',
          label: `검토 요청 (${pendingReviewPhotosByOthers.length}건 미검토)`,
          icon: AlertTriangleIcon,
          onClick: handleRequestReviewClick,
          disabled: isRequestingReview,
        }]
      : []),
    {
      id: 'delete',
      label: '점검 삭제',
      icon: TrashIcon,
      onClick: onDeleteInspection,
      danger: canDeleteInspection && !isLockedByLaterInspection && canManageInspections,
      disabled: !canDeleteInspection || isLockedByLaterInspection || !canManageInspections,
      disabledReason: !canManageInspections
        ? '점검 관리 권한이 없습니다.'
        : isLockedByLaterInspection
          ? '이후 차수의 점검이 존재하여 삭제할 수 없습니다.'
          : !canDeleteInspection
            ? '주 담당자 또는 조직 관리자만 삭제할 수 있습니다.'
            : undefined,
    },
  ];

  return (
    <div className="min-h-screen bg-surface">
      {/* SCHEDULED 배너 */}
      {inspection.status === 'SCHEDULED' && (
        <>
          {inspection.blockingInspectionId && (
            <div className="bg-yellow-50 border-b border-yellow-200 px-4 py-2.5 flex items-center justify-between gap-2">
              <span className="text-xs text-yellow-800 flex-1">
                이전 {inspection.blockingInspectionOrder ? `${inspection.blockingInspectionOrder}차 ` : ''}점검을 완료하거나 삭제해야 합니다.
              </span>
              <Link
                to={`/inspections/${inspection.blockingInspectionId}`}
                className="text-xs text-brand-600 font-medium flex-shrink-0"
              >
                이전 점검 보기 →
              </Link>
            </div>
          )}
          <div className="bg-violet-50 border-b border-violet-200 px-4 py-3 flex items-center justify-between gap-2">
            <span className="text-sm text-violet-700 flex-1">
              {inspection.scheduledAt && !canStartScheduledInspection(inspection.scheduledAt)
                ? '예약일 이후에 시작할 수 있습니다.'
                : '오늘 점검을 시작할 수 있습니다.'}
            </span>
            <div className="flex items-center gap-2 flex-shrink-0">
              {/* 아웃라인은 tone 축이 아직 안 담는다(tone=솔리드 채움 전용) — 색 계열만 violet으로 맞춘다. */}
              {canRequestScheduleChange && (
                <button
                  type="button"
                  onClick={() => setShowScheduleChangeModal(true)}
                  className="px-2.5 py-1.5 bg-white border border-violet-300 text-violet-700 text-xs rounded-control hover:bg-violet-50 font-medium"
                >
                  변경 요청
                </button>
              )}
              {onStartScheduled && (() => {
                const canStart = canStartScheduledInspection(inspection.scheduledAt) && !inspection.blockingInspectionId;
                return canStart ? (
                  <Button tone="violet" size="sm" onClick={onStartScheduled}>
                    시작
                  </Button>
                ) : null;
              })()}
            </div>
          </div>
        </>
      )}

      {/* 헤더 */}
      <header ref={headerRef} className="bg-white border-b border-line sticky top-14 z-40">
        <div className="px-4 py-3">
          <div className="flex items-center justify-between">
            {/* 왼쪽: 뒤로가기 */}
            <button
              type="button"
              onClick={onBack}
              className="text-ink-500 p-1 -ml-1"
            >
              <ChevronLeftIcon className="h-5 w-5" />
            </button>

            {/* 중앙: 제목 */}
            <div className="flex-1 text-center mx-2">
              <h1 className="font-semibold text-ink-900 truncate">
                {inspection.siteName} {inspection.sequenceNumber ?? 1}차 점검
              </h1>
              <span
                className={`text-xs ${
                  !canEdit
                    ? 'text-green-600'
                    : 'text-brand-600'
                }`}
              >
                {!canEdit ? '완료' : '진행중'}
              </span>
            </div>

            {/* 오른쪽: V2 토글 + 뷰어 + 메뉴 */}
            <div className="flex items-center gap-2">
              {/* 현재 보고 있는 사용자 */}
              {viewers.length > 0 && (
                <div className="relative group">
                  <button
                    type="button"
                    className="flex items-center gap-1 text-ink-500 p-1"
                  >
                    <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                    </svg>
                    <span className="text-xs">{viewers.length}</span>
                  </button>
                  {/* 툴팁 */}
                  <div className="absolute right-0 top-full mt-1 bg-ink-800 text-white text-xs rounded-card py-2 px-3 opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all duration-200 min-w-max z-50 shadow-card">
                    <div className="text-ink-400 mb-1">현재 보는 중</div>
                    <ul className="space-y-0.5">
                      {viewers.map((viewer) => (
                        <li key={viewer.id}>{viewer.name}</li>
                      ))}
                    </ul>
                  </div>
                </div>
              )}
              <button
                type="button"
                onClick={() => setIsMenuOpen(true)}
                className="text-ink-500 p-1 -mr-1"
              >
                <EllipsisHorizontalIcon className="h-5 w-5" />
              </button>
            </div>
          </div>
        </div>
      </header>

      {/* 메인 콘텐츠 (Pull to Refresh) */}
      <PullToRefresh onRefresh={onRefresh}>
        <main className="px-4 py-4 pb-24">
          {/* 기여자 정보 */}
          <div className="mb-4">
            <ContributorCount
              inspectorName={inspection.inspectorName}
              contributorCount={contributorCount}
            />
          </div>

          {/* 스케줄 변경 요청 패널 (SCHEDULED + 피처 플래그) */}
          {inspection.status === 'SCHEDULED' && scheduleChangeRequests.length > 0 && (
            <div className="mb-4">
              <ScheduleChangeRequestPanel
                inspectionId={inspection.id}
                requests={scheduleChangeRequests}
                isOwner={isOwner}
                onResponded={() => {
                  refetchScheduleChangeRequests();
                  queryClient.invalidateQueries({ queryKey: ['inspection', inspection.id] });
                }}
              />
            </div>
          )}

          {/* 이전 미해결 / 금번 점검 탭 (Sticky) */}
          {previousFindings.length > 0 && (
            <div ref={tabBarRef} className="sticky z-[35] bg-surface -mx-4 px-4" style={{ top: sectionStickyTop }}>
              <SectionTabBar
                tabs={[
                  { key: 'previous', label: '이전 미해결', badge: unreviewedFindingsCount || undefined },
                  { key: 'current', label: '금번 점검', badge: totalPhotos || undefined },
                ]}
                activeTab={activeInspectionTab}
                onTabChange={onInspectionTabChange}
              />
            </div>
          )}

          {/* 이전 미해결 콘텐츠 */}
          <section className="mb-6" style={{ display: previousFindings.length > 0 && activeInspectionTab === 'previous' ? '' : 'none' }}>
              <div className="mt-3 space-y-3">
                {previousFindings.map((finding) => (
                  <PreviousFindingCard
                    key={finding.id}
                    finding={finding}
                    currentInspectionId={inspection.id}
                    currentUserId={currentUserId}
                    isOwner={isOwner}
                    inspectorId={inspectorId}
                    hasReport={hasReport || isReadonly}
                    nextInspectionHasReport={inspection.nextInspectionHasReport}
                    isScheduled={isScheduled}
                    layout="compact"
                    nextInspectionDate={inspection.nextInspectionDate}
                    inspectionCreatedAt={inspection.createdAt}
                    onPhotoClick={(url) => {
                      setPhotoViewerState({
                        isOpen: true,
                        currentIndex: 0,
                        photos: [{ id: finding.photoId || finding.id, url }],
                        forceReadonly: true,
                        overrideInspectionId: finding.sourceInspectionId,
                      });
                    }}
                    onResolutionPhotosClick={(photos, startIndex) => {
                      setPhotoViewerState({
                        isOpen: true,
                        currentIndex: startIndex,
                        photos,
                        forceReadonly: true,
                        skipAnnotation: true,
                      });
                    }}
                    onDetailClick={() => setPreviousFindingDetail(finding)}
                    riskConfig={inspection.effectiveRiskAssessmentConfig}
                  />
                ))}
              </div>
            </section>

          {/* 금번 점검 사진 섹션 */}
          <section style={{ display: previousFindings.length === 0 || activeInspectionTab === 'current' ? '' : 'none' }}>
            {/* 섹션 헤더 (Sticky) */}
            <div className="sticky z-[34] bg-surface -mx-4 px-4 py-2 border-b border-line space-y-1.5" style={{ top: previousFindings.length > 0 ? tabBarBottom : sectionStickyTop }}>
              {/* 한 줄: 위험영역·그리기메모 토글 + 위험성평가 + 사진추가 + 우수사례 */}
              <div className="flex items-center gap-1 flex-nowrap">
                {/* 위험영역 토글 아이콘 버튼 */}
                <button
                  type="button"
                  onClick={() => setShowHazardOverlay((v) => !v)}
                  aria-label="위험영역 표시"
                  aria-pressed={showHazardOverlay}
                  title="위험영역 표시"
                  className={`p-1.5 rounded flex-shrink-0 ${showHazardOverlay ? 'bg-brand-100 text-brand-700' : 'bg-ink-100 text-ink-500'}`}
                >
                  {/* 경고 삼각형 아이콘 */}
                  <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" aria-hidden="true">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                  </svg>
                </button>

                {/* 그리기 메모 토글 아이콘 버튼 */}
                <button
                  type="button"
                  onClick={() => setShowAnnotationOverlay((v) => !v)}
                  aria-label="그리기 메모 표시"
                  aria-pressed={showAnnotationOverlay}
                  title="그리기 메모 표시"
                  className={`p-1.5 rounded flex-shrink-0 ${showAnnotationOverlay ? 'bg-brand-100 text-brand-700' : 'bg-ink-100 text-ink-500'}`}
                >
                  {/* 펜 아이콘 */}
                  <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" aria-hidden="true">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15.232 5.232l3.536 3.536m-2.036-5.036a2.5 2.5 0 113.536 3.536L6.5 21.036H3v-3.572L16.732 3.732z" />
                  </svg>
                </button>

                {/* 위험성평가 config 선택 드롭다운 (아이콘 모드) — 읽기 전용일 때는 고를 게 없어도 방식을 계속 보여준다 */}
                {shouldShowRiskConfigDropdown(allRiskConfigs.length, canEditRiskConfig) && (
                  <RiskConfigDropdown
                    inspectionId={inspection.id}
                    currentConfigId={inspection.riskAssessmentConfigId}
                    effectiveConfig={inspection.effectiveRiskAssessmentConfig}
                    configs={allRiskConfigs}
                    canEdit={canEditRiskConfig}
                    onSave={handleRiskConfigChange}
                    iconOnly
                  />
                )}

                <div className="flex-1" />

                {/* 수정 불가 안내 — 버튼 영역 오른쪽 정렬 전에 표시 */}
                {isEditDisabled && canEdit && (
                  <span className="text-xs text-amber-600 flex-shrink-0">
                    {hasReport ? '보고서 생성됨' : '잠김'}
                  </span>
                )}

                {/* 사진 추가 아이콘 버튼 */}
                {canEdit && (
                  <>
                    {(hasReport || isCompletionLocked) ? (
                      <PortalTooltip
                        content={hasReport ? '보고서가 생성되어 사진을 추가할 수 없습니다' : '점검완료 서명 후에는 사진을 추가할 수 없습니다'}
                        placement="top"
                        align="right"
                      >
                        <div>
                          <Button
                            type="button"
                            variant="primary"
                            size="sm"
                            iconOnly
                            className="flex-shrink-0"
                            onClick={onAddPhoto}
                            disabled={isLoading || isEditDisabled || isCompletionLocked}
                            aria-label="사진 추가"
                            title="사진 추가"
                          >
                            <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" aria-hidden="true">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 9a2 2 0 012-2h.93a2 2 0 001.664-.89l.812-1.22A2 2 0 0110.07 4h3.86a2 2 0 011.664.89l.812 1.22A2 2 0 0018.07 7H19a2 2 0 012 2v9a2 2 0 01-2 2H5a2 2 0 01-2-2V9z" />
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 13a3 3 0 11-6 0 3 3 0 016 0z" />
                            </svg>
                          </Button>
                        </div>
                      </PortalTooltip>
                    ) : (
                      <Button
                        type="button"
                        variant="primary"
                        size="sm"
                        iconOnly
                        className="flex-shrink-0"
                        onClick={onAddPhoto}
                        disabled={isLoading || isEditDisabled || isCompletionLocked}
                        aria-label="사진 추가"
                        title="사진 추가"
                      >
                        <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" aria-hidden="true">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 9a2 2 0 012-2h.93a2 2 0 001.664-.89l.812-1.22A2 2 0 0110.07 4h3.86a2 2 0 011.664.89l.812 1.22A2 2 0 0018.07 7H19a2 2 0 012 2v9a2 2 0 01-2 2H5a2 2 0 01-2-2V9z" />
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 13a3 3 0 11-6 0 3 3 0 016 0z" />
                        </svg>
                      </Button>
                    )}

                    {/* 우수사례 아이콘 버튼 */}
                    {onAddBestPracticePhoto && (
                      (hasReport || isCompletionLocked) ? (
                        <PortalTooltip
                          content={hasReport ? '보고서가 생성되어 사진을 추가할 수 없습니다' : '점검완료 서명 후에는 사진을 추가할 수 없습니다'}
                          placement="top"
                          align="right"
                        >
                          <div>
                            <Button
                              type="button"
                              variant="success"
                              size="sm"
                              iconOnly
                              className="flex-shrink-0"
                              onClick={onAddBestPracticePhoto}
                              disabled={isLoading || isEditDisabled || isCompletionLocked}
                              aria-label="우수사례 사진 추가"
                              title="우수사례 사진 추가"
                            >
                              <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" aria-hidden="true">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M14 10h4.764a2 2 0 011.789 2.894l-3.5 7A2 2 0 0115.263 21h-4.017c-.163 0-.326-.02-.485-.06L7 20m7-10V5a2 2 0 00-2-2h-.095c-.5 0-.905.405-.905.905 0 .714-.211 1.412-.608 2.006L7 11v9m7-10h-2M7 20H5a2 2 0 01-2-2v-6a2 2 0 012-2h2.5" />
                              </svg>
                            </Button>
                          </div>
                        </PortalTooltip>
                      ) : (
                        <Button
                          type="button"
                          variant="success"
                          size="sm"
                          iconOnly
                          className="flex-shrink-0"
                          onClick={onAddBestPracticePhoto}
                          disabled={isLoading || isEditDisabled || isCompletionLocked}
                          aria-label="우수사례 사진 추가"
                          title="우수사례 사진 추가"
                        >
                          <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" aria-hidden="true">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M14 10h4.764a2 2 0 011.789 2.894l-3.5 7A2 2 0 0115.263 21h-4.017c-.163 0-.326-.02-.485-.06L7 20m7-10V5a2 2 0 00-2-2h-.095c-.5 0-.905.405-.905.905 0 .714-.211 1.412-.608 2.006L7 11v9m7-10h-2M7 20H5a2 2 0 01-2-2v-6a2 2 0 012-2h2.5" />
                          </svg>
                        </Button>
                      )
                    )}
                  </>
                )}
              </div>
              {/* 점검완료 잠금 안내 */}
              {isCompletionLocked && !isEditDisabled && (
                <div className="flex items-center gap-2 px-3 py-1.5 bg-green-50 text-green-700 text-xs border-t border-green-200">
                  <svg className="w-4 h-4 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                  <span>점검완료 서명이 완료되었습니다. 수정하려면 점검완료를 취소해주세요.</span>
                </div>
              )}
            </div>

            {/* 사진 카드 리스트 */}
            {hazardPhotos.length === 0 && bestPracticePhotos.length === 0 ? (
              <PhotoEmptyState
                density="compact"
                title="아직 사진이 없습니다"
                description={
                  isEditDisabled
                    ? hasReport
                      ? '보고서가 생성되어 사진을 추가할 수 없습니다'
                      : '이후 차수의 점검이 존재하여 사진을 추가할 수 없습니다'
                    : '현장 사진을 추가해 AI 위험요인 분석을 시작해보세요'
                }
                action={
                  !isEditDisabled && !isReadonly ? (
                    <Button type="button" variant="primary" onClick={onAddPhoto}>
                      <PlusIcon className="h-4 w-4" />
                      첫 사진 추가하기
                    </Button>
                  ) : null
                }
              />
            ) : (
              <>
                {/* 위험요인 사진 섹션 */}
                {hazardPhotos.length > 0 && (
                  <div>
                    {bestPracticePhotos.length > 0 && (
                      <button
                        type="button"
                        onClick={() => setHazardSectionExpanded(!hazardSectionExpanded)}
                        className="flex items-center gap-2 mt-2 mb-2 px-1 group"
                      >
                        <span className="text-sm font-semibold text-ink-700">위험요인</span>
                        <span className="text-xs text-ink-500">({hazardPhotos.length}건)</span>
                        <svg className={`w-4 h-4 text-ink-400 transition-transform ${hazardSectionExpanded ? 'rotate-180' : ''}`}
                          fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                        </svg>
                      </button>
                    )}
                    {hazardSectionExpanded && (
                    <div className="space-y-3">
                      {hazardPhotos.map((photo, idx) => {
                        const findingStartIndex = 1;
                        const isPendingReview = !findingSelectionEnabled && !photo.isReviewed &&
                          photo.analysisStatus === 'COMPLETED' &&
                          canReviewPhoto(photo);

                        return (
                          <div
                            key={photo.id}
                            data-photo-id={photo.id}
                            data-pending-review={isPendingReview ? 'true' : undefined}
                          >
                            <PhotoCardVertical
                                photo={photo}
                                findings={photo.findings}
                                index={idx + 1}
                                findingStartIndex={findingStartIndex}
                                focusCategories={focusCategories}
                                isReviewed={photo.isReviewed}
                                showHazardOverlay={showHazardOverlay}
                                onReviewToggle={
                                  !findingSelectionEnabled && canReviewPhoto(photo)
                                    ? (checked) => onPhotoReview(photo.id, checked)
                                    : undefined
                                }
                                reviewDisabled={!findingSelectionEnabled && !canReviewPhoto(photo) && canEdit}
                                headerAction={
                                  <button
                                    type="button"
                                    onClick={() => {
                                      setSelectedPhotoId(photo.id);
                                      setIsActionSheetOpen(true);
                                    }}
                                    className="p-1 text-ink-500 hover:text-ink-700 rounded hover:bg-ink-200 transition-colors"
                                  >
                                    <EllipsisHorizontalIcon className="h-5 w-5" />
                                  </button>
                                }
                                selectable={canReviewPhoto(photo) && !isCompletionLocked}
                                isSelected={selectedPhotoIds.has(photo.id)}
                                onSelectionChange={(checked) => handleSelectionChange(photo.id, checked)}
                                showAddedBy={(inspection.contributors?.length ?? 0) > 1}
                                highlightedFindingId={highlightedFindingId ?? undefined}
                                onFindingClick={(id) => setHighlightedFindingId(id === highlightedFindingId ? null : id)}
                                editable={canEditFinding}
                                editHandlers={canEditFinding ? findingEditHandlers : undefined}
                                hazardEdits={Array.from(hazardEdits.values())}
                                onHazardChange={canEditFinding ? handleHazardChange : undefined}
                                onHazardChangeEnd={canEditFinding ? handleHazardChangeEnd : undefined}
                                onHazardDeleteSave={canEditFinding ? handleHazardDeleteSave : undefined}
                                onHazardRestore={canEditFinding ? handleHazardRestore : undefined}
                                showAnnotationOverlay={showAnnotationOverlay}
                                companyId={companyId}
                                siteId={inspection.siteId}
                                inspectionId={inspection.id}
                                onResolveHazard={
                                  !(hasReport || isCompletionLocked) && canEdit
                                    ? (findingId?: string) => setHazardResolutionState({ isOpen: true, photoId: photo.id, preSelectedFindingId: findingId })
                                    : undefined
                                }
                                hasReport={hasReport}
                                isCompletionLocked={isCompletionLocked}
                                hazardResolutionEnabled={true}
                                findingSelectionEnabled={findingSelectionEnabled}
                                onToggleMeasureSelection={isCompletionLocked ? undefined : onToggleMeasureSelection}
                                currentUserId={currentUserId}
                                isMyCompleted={completion?.isMyCompleted ?? false}
                                isPrimary={isPrimary}
                                isOwner={isOwner}
                                riskConfig={inspection.effectiveRiskAssessmentConfig}
                              />
                          </div>
                        );
                      })}
                    </div>
                    )}
                  </div>
                )}

                {/* 우수사례 사진 섹션 */}
                {bestPracticePhotos.length > 0 && (
                  <div className="mt-4">
                    <button
                      type="button"
                      onClick={() => setBpSectionExpanded(!bpSectionExpanded)}
                      className="flex items-center gap-2 mb-2 px-1 group"
                    >
                      <svg className="w-4 h-4 text-emerald-500" fill="currentColor" viewBox="0 0 24 24">
                        <path d="M14 10h4.764a2 2 0 011.789 2.894l-3.5 7A2 2 0 0115.263 21h-4.017c-.163 0-.326-.02-.485-.06L7 20m7-10V5a2 2 0 00-2-2h-.095c-.5 0-.905.405-.905.905 0 .714-.211 1.412-.608 2.006L7 11v9m7-10h-2M7 20H5a2 2 0 01-2-2v-6a2 2 0 012-2h2.5" />
                      </svg>
                      <span className="text-sm font-semibold text-emerald-700">우수사례</span>
                      <span className="text-xs text-emerald-600">({bestPracticePhotos.length}건)</span>
                      <svg className={`w-4 h-4 text-emerald-400 transition-transform ${bpSectionExpanded ? 'rotate-180' : ''}`}
                        fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                      </svg>
                    </button>
                    {bpSectionExpanded && (
                    <div className="space-y-3">
                      {bestPracticePhotos.map((photo, idx) => (
                        <div key={photo.id} data-photo-id={photo.id}>
                          <PhotoCardVertical
                              photo={photo}
                              findings={photo.findings}
                              index={idx + 1}
                              findingStartIndex={1}
                              isReviewed={photo.isReviewed}
                              showHazardOverlay={false}
                              onReviewToggle={
                                !findingSelectionEnabled && canReviewPhoto(photo)
                                  ? (checked) => onPhotoReview(photo.id, checked)
                                  : undefined
                              }
                              reviewDisabled={!findingSelectionEnabled && !canReviewPhoto(photo) && canEdit}
                              headerAction={
                                <button
                                  type="button"
                                  onClick={() => {
                                    setSelectedPhotoId(photo.id);
                                    setIsActionSheetOpen(true);
                                  }}
                                  className="p-1 text-ink-500 hover:text-ink-700 rounded hover:bg-ink-200 transition-colors"
                                >
                                  <EllipsisHorizontalIcon className="h-5 w-5" />
                                </button>
                              }
                              selectable={canReviewPhoto(photo) && !isCompletionLocked}
                              isSelected={selectedPhotoIds.has(photo.id)}
                              onSelectionChange={(checked) => handleSelectionChange(photo.id, checked)}
                              showAddedBy={(inspection.contributors?.length ?? 0) > 1}
                              editable={canEditFinding}
                              editHandlers={canEditFinding ? findingEditHandlers : undefined}
                              showAnnotationOverlay={showAnnotationOverlay}
                              companyId={companyId}
                              siteId={inspection.siteId}
                              inspectionId={inspection.id}
                              hasReport={isEditDisabled}
                              hazardResolutionEnabled={false}
                              onAddFinding={
                                !isEditDisabled && onAddFinding && canEdit && !isCompletionLocked
                                  ? () => handleOpenAddFindingSheet(photo.id)
                                  : undefined
                              }
                              findingSelectionEnabled={findingSelectionEnabled}
                              onToggleMeasureSelection={isCompletionLocked ? undefined : onToggleMeasureSelection}
                              onPhotoMetaUpdate={onPhotoMetaUpdate}
                              currentUserId={currentUserId}
                              isMyCompleted={completion?.isMyCompleted ?? false}
                              isPrimary={isPrimary}
                              isOwner={isOwner}
                              riskConfig={inspection.effectiveRiskAssessmentConfig}
                            />
                        </div>
                      ))}
                    </div>
                    )}
                  </div>
                )}
              </>
            )}
          </section>
        </main>
      </PullToRefresh>

      {/* 하단 액션 바 */}
      <InspectionActionBar
        onAddPhoto={onAddPhoto}
        onOpenCamera={onOpenCamera}
        onSelectiveReview={handleSelectiveReview}
        onSelectiveDelete={handleSelectiveDelete}
        onSelectiveReanalyze={handleSelectiveReanalyze}
        onGenerateReport={() => setShowReportModal(true)}
        onSelectAll={handleSelectAll}
        findingSelectionEnabled={findingSelectionEnabled}
        reviewProgress={{
          reviewed: reviewedPhotos.length,
          total: reviewablePhotos.length,
          analyzing: analyzingPhotos.length,
          pendingReview: pendingReviewPhotos.length,
        }}
        selectedCount={selectedCount}
        totalCount={selectablePhotos.length}
        isAllSelected={isAllSelected}
        canGenerateReport={canGenerateReport}
        reportDisabledReason={reportGate.disabledReason ?? undefined}
        reportLabel={
          completion?.completionSignatureEnabled && !completion?.allCompleted && completion.totalCount > 1
            ? `보고서 생성 (${signatureProgressLabel(completion)} 완료)`
            : findingSelectionEnabled && totalFindingsCount > 0
              ? `보고서 생성 (${includedFindingsCount}/${totalFindingsCount}건 포함)`
              : undefined
        }
        isLoading={isLoading}
        isReanalyzing={isReanalyzing}
        reanalysisProgress={reanalysisProgress}
        isCompleted={!canEdit}
        isEditDisabled={isEditDisabled || isCompletionLocked}
        completion={completion ? {
          enabled: completion.completionSignatureEnabled,
          canSign: completion.canSign,
          canCancel: completion.canCancel,
          canRequest: completion.canRequest,
          isSubmitting: completion.isSubmitting,
          isMyCompleted: completion.isMyCompleted,
          completedCount: completion.completedCount,
          totalCount: completion.totalCount,
          signDisabledReason: completion.signDisabledReason,
          onSign: () => setShowSignatureModal(true),
          onManage: () => {
            if (completion.totalCount <= 1) {
              completion.handleCancelMyCompletion();
            } else {
              setShowCompletionActionModal(true);
            }
          },
        } : undefined}
        hasReport={hasReport}
        enableAIAnalysis={enableAIAnalysis}
        setEnableAIAnalysis={setEnableAIAnalysis}
        isScheduled={isScheduled}
        onStartScheduled={onStartScheduled}
        scheduledAt={inspection.scheduledAt}
      />

      {/* 사진 액션 시트 */}
      <ActionSheet
        isOpen={isActionSheetOpen}
        onClose={() => setIsActionSheetOpen(false)}
        items={getPhotoActionItems()}
      />

      {/* 메인 메뉴 액션 시트 */}
      <ActionSheet
        isOpen={isMenuOpen}
        onClose={() => setIsMenuOpen(false)}
        title="점검 메뉴"
        items={menuItems}
      />

      {/* 사진 뷰어 (그리기 기능 포함) */}
      <PhotoViewer
        photos={photoViewerState.photos}
        currentIndex={photoViewerState.currentIndex}
        isOpen={photoViewerState.isOpen}
        onClose={() => setPhotoViewerState((s) => ({ ...s, isOpen: false }))}
        onIndexChange={(index) =>
          setPhotoViewerState((s) => ({ ...s, currentIndex: index }))
        }
        annotation={{
          companyId: companyId || '',
          inspectionId: photoViewerState.overrideInspectionId || inspection.id,
          readonly: isReadonly || isEditDisabled || isCompletionLocked || !!photoViewerState.forceReadonly,
          initialShowAnnotation: !photoViewerState.forceReadonly,
          skipAnnotation: !!photoViewerState.skipAnnotation,
        }}
      />

      {/* 위험 영역 에디터 */}
      <PhotoViewerWithEditor
        photos={photosWithFindings}
        currentIndex={hazardEditorState.currentIndex}
        isOpen={hazardEditorState.isOpen}
        onClose={() => setHazardEditorState((s) => ({ ...s, isOpen: false }))}
        onIndexChange={(index) =>
          setHazardEditorState((s) => ({ ...s, currentIndex: index }))
        }
        onHazardChange={handleHazardChange}
        onHazardRestore={handleHazardRestore}
        hazardEdits={Array.from(hazardEdits.values())}
      />

      {/* 보고서 생성 모달 */}
      <ReportTypeSelectModal
        isOpen={showReportModal}
        inspectionId={inspection.id}
        companyId={companyId || ''}
        siteId={inspection.siteId}
        onClose={() => setShowReportModal(false)}
      />

      {/* 일괄 작업 확인 바텀시트 */}
      <BulkActionConfirmBottomSheet
        isOpen={bulkActionSheet.isOpen}
        onClose={closeBulkActionSheet}
        onConfirm={handleBulkActionConfirm}
        actionType={bulkActionSheet.actionType}
        selectedCount={selectedCount}
        isLoading={bulkActionSheet.isLoading}
      />

      {/* 발견사항 추가 시트 */}
      {addFindingSheetState.photoId && (
        <AddFindingSheet
          isOpen={addFindingSheetState.isOpen}
          onClose={handleCloseAddFindingSheet}
          photoId={addFindingSheetState.photoId}
          onSubmit={handleAddFindingSubmit}
          isLoading={addFindingSheetState.isLoading}
          mode={addFindingSheetState.mode}
        />
      )}

      {/* 이전 미해결 상세 모달 */}
      {previousFindingDetail && (
        <PreviousFindingDetailModal
          finding={previousFindingDetail}
          onClose={() => setPreviousFindingDetail(null)}
          riskConfig={inspection.effectiveRiskAssessmentConfig}
        />
      )}

      {/* 위험요인 해소 모달 */}
      {hazardResolutionState.isOpen && hazardResolutionState.photoId && companyId && (
        <HazardResolutionModal
          isOpen={hazardResolutionState.isOpen}
          onClose={() => setHazardResolutionState({ isOpen: false, photoId: null })}
          inspectionId={inspection.id}
          companyId={companyId}
          findings={
            activeFindings(
              inspection.photos.find(p => p.id === hazardResolutionState.photoId)?.findings ?? [],
            ).map(f => ({ ...f, measures: f.aiAnalysisDetail?.recommendation?.measures }))
          }
          preSelectedFindingId={hazardResolutionState.preSelectedFindingId}
          riskConfig={inspection.effectiveRiskAssessmentConfig}
        />
      )}

      {/* 점검 담당자 편집 모달 */}
      {companyId && (
        <ParticipantsEditModal
          isOpen={participantsModalOpen}
          onClose={() => setParticipantsModalOpen(false)}
          inspectionId={inspection.id}
          companyId={companyId}
          siteId={inspection.siteId}
          currentParticipants={currentParticipants}
          currentInspectorId={inspectorId ?? null}
          canEdit={canEditParticipants}
        />
      )}

      {/* 점검완료 서명 모달 */}
      {completion?.completionSignatureEnabled && (
        <SignatureModal
          isOpen={showSignatureModal}
          onClose={() => setShowSignatureModal(false)}
          onSubmit={async (signatureData) => {
            await completion.handleCompleteMyInspection({ signatureData });
            setShowSignatureModal(false);
          }}
          isSubmitting={completion.isSubmitting}
          error={completion.error}
          variant="bottom"
        />
      )}

      {/* 점검완료 취소/요청 모달 */}
      {/*
        처리(취소·요청) 중에는 닫기 수단을 전부 막는다 — 종전엔 바깥 클릭으로 도중에 닫혔다.
        모바일에서 바닥·좌우에 밀착하는 시트 모양(sm 이상은 가운데)은 `SignatureModal`의 bottom 갈래와
        같은 패널 클래스로 낸다 — 방법과 제약은 `MOBILE_BOTTOM_PANEL_CLASS` docblock.
      */}
      {showCompletionActionModal && completion?.completionSignatureEnabled && (
        <Modal
          open
          onClose={() => setShowCompletionActionModal(false)}
          title={completion.totalCount <= 1 ? '점검완료 취소' : '점검완료 취소/요청'}
          size="sm"
          overlayZClassName={MODAL_LAYER.sub}
          panelClassName={MOBILE_BOTTOM_PANEL_CLASS}
          closeLocked={completion.isSubmitting}
        >
              <div className="space-y-3">
                {/* 완료 현황 */}
                <div className="bg-surface-soft rounded-card p-3 text-sm">
                  <p className="text-ink-600">
                    점검완료 현황: <span className="font-medium text-ink-900">{completion.completedCount}/{completion.totalCount}</span>
                  </p>
                  {completion.pendingContributors.length > 0 && (
                    <p className="mt-1 text-ink-500">
                      미완료: {completion.pendingContributors.map(c => c.userName).join(', ')}
                    </p>
                  )}
                </div>

                {/* 점검완료 취소 */}
                {completion.canCancel && (
                  <button
                    type="button"
                    disabled={completion.isSubmitting}
                    onClick={() => setShowCancelConfirm(true)}
                    className="w-full flex items-center gap-3 px-4 py-3 text-left text-sm font-medium text-red-700 bg-red-50 active:bg-red-100 rounded-card disabled:opacity-50"
                  >
                    <svg className="w-5 h-5 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                    </svg>
                    내 점검완료 취소
                  </button>
                )}

                {/* 점검완료 요청 */}
                {completion.canRequest && (
                  <button
                    type="button"
                    disabled={completion.isSubmitting}
                    onClick={async () => {
                      try {
                        const result = await completion.handleRequestCompletion();
                        if (result) {
                          const count = result.requestedCount ?? result.notifiedUsers?.length ?? 0;
                          showToast(`${count}명에게 점검완료 요청을 보냈습니다.`, 'success');
                        }
                        setShowCompletionActionModal(false);
                      } catch {
                        // error is set in the hook
                      }
                    }}
                    className="w-full flex items-center gap-3 px-4 py-3 text-left text-sm font-medium text-amber-700 bg-amber-50 active:bg-amber-100 rounded-card disabled:opacity-50"
                  >
                    <svg className="w-5 h-5 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 17h5l-1.405-1.405A2.032 2.032 0 0118 14.158V11a6.002 6.002 0 00-4-5.659V5a2 2 0 10-4 0v.341C7.67 6.165 6 8.388 6 11v3.159c0 .538-.214 1.055-.595 1.436L4 17h5m6 0v1a3 3 0 11-6 0v-1m6 0H9" />
                    </svg>
                    미완료 참여자에게 요청
                  </button>
                )}

                {completion.error && (
                  <p className="text-sm text-red-600">{completion.error}</p>
                )}
              </div>
        </Modal>
      )}
      {/* 스케줄 변경 요청 모달 */}
      {canRequestScheduleChange && (
        <ScheduleChangeRequestModal
          isOpen={showScheduleChangeModal}
          onClose={() => setShowScheduleChangeModal(false)}
          inspectionId={inspection.id}
          currentScheduledAt={inspection.scheduledAt}
          onSuccess={() => {
            refetchScheduleChangeRequests();
          }}
        />
      )}

      {/* 점검완료 취소 확인 모달 */}
      <ConfirmDialog
        isOpen={showCancelConfirm}
        onClose={() => setShowCancelConfirm(false)}
        onConfirm={async () => {
          try {
            await completion?.handleCancelMyCompletion();
            setShowCancelConfirm(false);
            setShowCompletionActionModal(false);
          } catch {
            // error is set in the hook
          }
        }}
        title="점검완료 취소"
        message="점검완료를 취소하시겠습니까?"
        confirmText="취소하기"
        cancelText="닫기"
        confirmStyle="danger"
        isLoading={completion?.isSubmitting}
      />

    </div>
  );
}

export default InspectionDetailPhone;
